import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { faqs } from "@/mocks/faqs";
import { siteInfo } from "@/mocks/site";
import { useSEO, SITE_ORIGIN } from "@/hooks/useSEO";

const groups = ["使用指南", "内容与来源", "联系与纠错"];

export default function FaqPage() {
  useSEO({
    title: "常见问题 - 黑料百科使用与内容说明",
    description:
      "黑料百科常见问题汇总：词条是什么、如何搜索人物、热度怎么算、信息来源与更新频率、隐私边界以及纠错反馈方式，一次看清站点的使用规则。",
    keywords: "黑料百科常见问题,词条说明,信息来源,更新频率,纠错反馈",
    path: "/faq",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        url: `${SITE_ORIGIN}/faq`,
        inLanguage: "zh-CN",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
          { "@type": "ListItem", position: 2, name: "常见问题", item: `${SITE_ORIGIN}/faq` },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100/60 px-4 py-4 md:px-6">
        <Breadcrumb items={[{ label: "常见问题" }]} />
      </div>

      <section className="border-b border-background-200 bg-background-50 px-4 py-10 md:px-6 md:py-14">
        <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-100 text-primary-700">
          <i className="ri-question-line text-2xl"></i>
        </span>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">常见问题</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
          下面这些问题最常被问到。如果还是没有答案，直接给我们留言就好。
        </p>
      </section>

      <div className="grid grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-12 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="space-y-10">
          {groups.map((group) => {
            const items = faqs.filter((item) => item.group === group);
            if (items.length === 0) return null;
            return (
              <section key={group}>
                <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-price-tag-3-line text-accent-600"></i>
                  {group}
                  <span className="text-xs font-normal text-foreground-400">{items.length} 条</span>
                </h2>
                <FaqAccordion items={items} defaultOpenId={group === groups[0] ? items[0]?.id : undefined} />
              </section>
            );
          })}
        </div>

        <aside className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">还有疑问？</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              选择「内容纠错」或「话题推荐」类型留言，我们会逐一核对并回复。
            </p>
            <Link
              to="/contact"
              className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-chat-3-line"></i>
              去留言
            </Link>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">联系方式</h2>
            <ul className="mt-4 space-y-3 text-sm text-foreground-600">
              <li className="flex items-start gap-2">
                <i className="ri-mail-line mt-0.5 text-primary-500"></i>
                <a href={`mailto:${siteInfo.email}`} className="transition-colors hover:text-primary-700">
                  {siteInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-briefcase-line mt-0.5 text-primary-500"></i>
                <a href={`mailto:${siteInfo.businessEmail}`} className="transition-colors hover:text-primary-700">
                  {siteInfo.businessEmail}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-time-line mt-0.5 text-primary-500"></i>
                {siteInfo.replyTime}
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">继续浏览</h2>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li>
                <Link to="/ranking" className="flex items-center gap-2 text-foreground-700 transition-colors hover:text-primary-700">
                  <i className="ri-fire-line text-accent-600"></i> 热度榜单
                </Link>
              </li>
              <li>
                <Link to="/category/star" className="flex items-center gap-2 text-foreground-700 transition-colors hover:text-primary-700">
                  <i className="ri-layout-grid-line text-accent-600"></i> 词条分类
                </Link>
              </li>
              <li>
                <Link to="/about" className="flex items-center gap-2 text-foreground-700 transition-colors hover:text-primary-700">
                  <i className="ri-information-line text-accent-600"></i> 关于我们
                </Link>
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}